import { v4 as uuidv4 } from 'uuid';
import api from '.';

const AI_GATEWAY_BASE = '/ai-gateway';

const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

export async function generateWorkflowFunction({ nodeId, task, samplePayload, currentCode, model, routerMarker, idempotencyKey }) {
  // ключ контролирует вызывающий код, чтобы повторная отправка той же генерации
  // дедуплицировалась бэком; на всякий случай генерируем запасной
  const body = { nodeId, task, idempotencyKey: idempotencyKey || uuidv4() };
  if (samplePayload !== undefined) {
    body.samplePayload = samplePayload;
  }
  if (currentCode) {
    body.currentCode = currentCode;
  }
  if (routerMarker) {
    body.routerMarker = routerMarker;
  }

  if (model) {
    body.model = model;
  }

  const { data } = await api.post(
    `${AI_GATEWAY_BASE}/agents/workflow-function/generate`,
    body,
    undefined,
    false,
  );
  return data;
}

/**
 * Ставит задачу агенту конструктора компонентов: по формулировке пользователя
 * собрать конфиг компонента, не выходя за его JSON Schema.
 *
 * Агент и модель приходят из раздела Settings → ИИ-агенты (`routerMarker` +
 * `model`); без них бэк возьмёт агента по умолчанию. Ключ и лимиты остаются на
 * стороне гейтвея — фронт их не видит и обойти не может.
 * @param {object} params
 * @param {string} params.configId логический ключ настраиваемого компонента
 * @param {string} params.componentType id типа компонента ('table' | 'form')
 * @param {object} params.schema JSON Schema конфига — контракт с моделью
 * @param {object} params.currentConfig что настроено сейчас
 * @param {object} params.componentDescription колонки, поля строки, провайдеры
 * @param {number[]} [params.attributeSetIds] наборы атрибутов из загруженных
 *   строк: по ним бэкенд собирает список атрибутов, которые можно вывести
 * @param {string[]} [params.attributeSetIdentifiers] маркеры тех же наборов —
 *   для записей, у которых набор прицеплен к форме, а не к ним самим
 * @param {string} params.prompt формулировка пользователя
 * @param {Array} [params.history] предыдущие ходы разговора
 * @param {string} [params.routerMarker]
 * @param {string} [params.model]
 * @param {string} [params.idempotencyKey]
 * @returns {Promise<{ jobId: number }>}
 */
export async function generateComponentConfig({
  configId,
  componentType,
  schema,
  currentConfig,
  componentDescription,
  attributeSetIds,
  attributeSetIdentifiers,
  attributeMarkers,
  prompt,
  history,
  routerMarker,
  model,
  idempotencyKey,
}) {
  // ключ контролирует вызывающий код, чтобы повторная отправка того же запроса
  // дедуплицировалась бэком; на всякий случай генерируем запасной
  // componentType бэкенду не нужен: тип компонента задан его же схемой,
  // которая приезжает в `schema`.
  const body = {
    configId,
    schema,
    currentConfig,
    componentDescription,
    prompt,
    idempotencyKey: idempotencyKey || uuidv4(),
  };
  if (attributeSetIds?.length) {
    body.attributeSetIds = attributeSetIds;
  }
  if (attributeSetIdentifiers?.length) {
    body.attributeSetIdentifiers = attributeSetIdentifiers;
  }
  if (attributeMarkers?.length) {
    body.attributeMarkers = attributeMarkers;
  }
  if (history?.length) {
    body.history = history;
  }
  if (routerMarker) {
    body.routerMarker = routerMarker;
  }
  if (model) {
    body.model = model;
  }

  // trimStrings=false: формулировка пользователя и конфиг уходят как есть
  const { data } = await api.post(
    `${AI_GATEWAY_BASE}/agents/ui-component-config/suggest`,
    body,
    undefined,
    false,
  );
  return data;
}

/**
 * Результат генерации агента конструктора компонентов.
 * @typedef {object} ComponentConfigResult
 * @property {object|null} suggested предложенный конфиг; null = агент ничего не предложил
 * @property {string|null} warning почему просьбу не удалось выполнить целиком,
 *   словами модели на языке запроса
 * @property {string|null} warningCode код фразы, когда её писала не модель, а
 *   сервер; текст подставляет интерфейс (`configurable/warningText.js`)
 */

/**
 * Нормализует output задачи к ответу агента конструктора.
 *
 * `suggested === null` — законный исход: модель обязана отказаться, если
 * просьба не выражается схемой. Панель по этому признаку предлагает поискать
 * источник данных в API, поэтому отказ нельзя схлопывать в пустой объект.
 * @returns {ComponentConfigResult}
 */
export function extractComponentConfigResult(output) {
  if (!output || typeof output !== 'object') {
    return { suggested: null, warning: null, providers: [] };
  }
  const config = output.config && typeof output.config === 'object' ? output.config : null;
  const warning = typeof output.warning === 'string' && output.warning ? output.warning : null;
  // Фразу пишет модель на языке запроса. Когда писать её было некому (попытки
  // кончились), приходит код, а слова подставляет интерфейс из своего словаря.
  const warningCode = typeof output.warningCode === 'string' && output.warningCode
    ? output.warningCode
    : null;

  // Пустой конфиг вместе с warning — это отказ, а не «настроек нет».
  // Пропусти мы его дальше как предложение, панель предложила бы применить
  // пустоту, то есть стереть текущую настройку. Без warning пустой конфиг
  // законен: пользователь мог попросить сбросить всё.
  const isRefusal = (warning !== null || warningCode !== null)
    && config !== null
    && Object.keys(config).length === 0;

  // Рецепты собирает только агентный прогон, и только когда прежний агент
  // отказался. Панель показывает их отдельной карточкой: работать они начинают
  // сразу, а на сервер уезжают после подтверждения.
  const providers = Array.isArray(output.providers) ? output.providers : [];

  return {
    suggested: isRefusal ? null : config, warning, warningCode, providers,
  };
}

/**
 * Ставит задачу агенту подбора источника: найти в admin API ручку, из которой
 * возьмётся значение, которого нет в строке таблицы.
 *
 * Агент никуда не звонит — он только называет ручку и говорит, чем заполнить
 * её параметры. Вызвать её на живой строке и показать настоящие значения
 * должен фронт: так пользователь выбирает поле, глядя на данные.
 * @param {object} params
 * @param {string} params.prompt формулировка пользователя
 * @param {string} [params.configId] ключ экрана: по нему бэкенд знает, записями
 *   какого раздела являются строки, и отсеивает кандидата с идентификатором
 *   чужой сущности
 * @param {object} params.componentDescription поля строки и колонки таблицы
 * @param {string} [params.routerMarker]
 * @param {string} [params.model]
 * @param {string} [params.idempotencyKey]
 * @returns {Promise<{ jobId: number }>}
 */
export async function discoverUiProvider({
  prompt,
  configId,
  componentDescription,
  routerMarker,
  model,
  idempotencyKey,
}) {
  const body = {
    prompt,
    componentDescription,
    idempotencyKey: idempotencyKey || uuidv4(),
  };
  if (configId) {
    body.configId = configId;
  }
  if (routerMarker) {
    body.routerMarker = routerMarker;
  }
  if (model) {
    body.model = model;
  }

  // trimStrings=false: формулировка пользователя уходит как есть
  const { data } = await api.post(
    `${AI_GATEWAY_BASE}/agents/ui-provider-discovery/discover`,
    body,
    undefined,
    false,
  );
  return data;
}

/**
 * Аккаунт ИИ-провайдера (AiProviderAccountResponseDto). Аккаунтов может быть много,
 * идентификатор — уникальный неизменяемый marker; apiKey бэк отдаёт целиком.
 * @typedef {object} AiProviderAccount
 * @property {string} marker уникальный неизменяемый идентификатор
 * @property {string|null} name имя для UI
 * @property {string} providerKey провайдер ('openrouter' | 'mock')
 * @property {boolean} hasApiKey задан ли ключ
 * @property {string|null} apiKey полный ключ (бэк отдаёт наружу)
 * @property {string|null} baseUrl
 * @property {number|null} requestTimeoutMs
 * @property {string[]} allowedModels
 * @property {string|null} defaultModel
 * @property {number|null} tokenLimit лимит токенов; null = без лимита
 * @property {string|null} tokensResetAt
 * @property {number|null} costLimitUsd лимит в USD; null = без лимита
 * @property {string|null} costResetAt
 * @property {number} agentRunTimeoutSec время одного запроса агента, сек
 * @property {number|null} agentRunCostLimitUsd лимит одного запроса агента, USD; null = без лимита
 */

/**
 * Страница списка аккаунтов (ItemsWithTotal).
 * @typedef {object} AiProviderAccountsPage
 * @property {AiProviderAccount[]} items
 * @property {number} total
 */

/**
 * Модель LLM из каталога провайдера.
 * @typedef {object} LlmModel
 * @property {string} id
 * @property {string} label
 * @property {boolean} isFree
 * @property {boolean} [isRecommended] бейдж «рекомендуем» в выборе моделей
 * @property {number} [promptUsdPerToken]
 * @property {number} [completionUsdPerToken]
 * @property {number} [contextLength]
 */

/**
 * Возвращает страницу списка аккаунтов ИИ-провайдеров (с пагинацией limit/offset).
 * @param {{ limit?: number, offset?: number, signal?: AbortSignal }} [params]
 * @returns {Promise<AiProviderAccountsPage>}
 */
export async function getAiRouters({ limit = 30, offset = 0, signal } = {}) {
  const { data } = await api.get(`${AI_GATEWAY_BASE}/routers`, { params: { limit, offset }, signal });
  return data;
}

/**
 * Возвращает один аккаунт по marker.
 * @param {string} marker
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<AiProviderAccount>}
 */
export async function getAiRouter(marker, { signal } = {}) {
  const { data } = await api.get(`${AI_GATEWAY_BASE}/routers/${marker}`, { signal });
  return data;
}

/**
 * Тело создания аккаунта (CreateAiRouterDto). providerKey и marker обязательны и неизменяемы.
 * @typedef {object} CreateAiRouter
 * @property {string} providerKey 'openrouter' | 'mock'
 * @property {string} marker уникальный, ^[A-Za-z]+[a-zA-Z0-9_-]*$
 * @property {string} [name]
 * @property {string} [apiKey]
 * @property {string} [baseUrl]
 * @property {number} [requestTimeoutMs]
 * @property {string[]} [allowedModels]
 * @property {string} [defaultModel]
 * @property {number|null} [tokenLimit]
 * @property {number|null} [costLimitUsd]
 * @property {number} [agentRunTimeoutSec]
 * @property {number|null} [agentRunCostLimitUsd]
 */

/**
 * Тело обновления аккаунта (UpdateAiRouterDto); все поля опциональны, providerKey/marker неизменяемы.
 * tokenLimit/costLimitUsd: число = лимит, null = снять лимит. resetTokenUsage/resetCostUsage обнуляют счётчики.
 * @typedef {object} UpdateAiRouter
 * @property {string} [name]
 * @property {string} [apiKey]
 * @property {string} [baseUrl]
 * @property {number} [requestTimeoutMs]
 * @property {string[]} [allowedModels]
 * @property {string} [defaultModel]
 * @property {number|null} [tokenLimit]
 * @property {number|null} [costLimitUsd]
 * @property {number} [agentRunTimeoutSec]
 * @property {number|null} [agentRunCostLimitUsd]
 * @property {boolean} [resetTokenUsage]
 * @property {boolean} [resetCostUsage]
 */

/**
 * Потребление токенов и USD аккаунта (AiTokenUsageDto).
 * @typedef {object} AiTokenUsage
 * @property {number|null} tokenLimit лимит токенов; null = без лимита
 * @property {number} consumedTokens потрачено токенов
 * @property {number|null} remainingTokens остаток токенов; null = без лимита
 * @property {string|null} tokensResetAt ISO-дата последнего обнуления счётчика токенов
 * @property {number|null} costLimitUsd лимит в USD; null = без лимита
 * @property {number} consumedCostUsd потрачено USD
 * @property {number|null} remainingCostUsd остаток USD; null = без лимита
 * @property {string|null} costResetAt ISO-дата последнего обнуления USD-счётчика
 * @property {boolean} isExhausted любой из лимитов исчерпан
 */

/**
 * Возвращает потребление токенов/USD конкретного аккаунта.
 * @param {string} marker
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<AiTokenUsage>}
 */
export async function getRouterUsage(marker, { signal } = {}) {
  const { data } = await api.get(`${AI_GATEWAY_BASE}/routers/${marker}/usage`, { signal });
  return data;
}

/**
 * Создаёт аккаунт ИИ-провайдера и возвращает его DTO.
 * @param {CreateAiRouter} body
 * @returns {Promise<AiProviderAccount>}
 */
export async function createAiRouter(body) {
  // trimStrings=false: apiKey и прочие значения не должны обрезаться по краям
  const { data } = await api.post(`${AI_GATEWAY_BASE}/routers`, body, undefined, false);
  return data;
}

/**
 * Обновляет аккаунт по marker (частичное обновление) и возвращает свежий DTO.
 * @param {string} marker
 * @param {UpdateAiRouter} body
 * @returns {Promise<AiProviderAccount>}
 */
export async function updateAiRouter(marker, body) {
  // trimStrings=false: apiKey и прочие значения не должны обрезаться по краям
  const { data } = await api.put(`${AI_GATEWAY_BASE}/routers/${marker}`, body, undefined, false);
  return data;
}

/**
 * Удаляет аккаунт по marker (бэк отвечает 204 No Content).
 * @param {string} marker
 * @returns {Promise<void>}
 */
export async function deleteAiRouter(marker) {
  await api.remove(`${AI_GATEWAY_BASE}/routers/${marker}`);
}

/**
 * Тянет каталог моделей конкретного провайдера для экрана курации.
 * freeOnly фильтрует выдачу на стороне бэка (актуально для openrouter).
 * toolsOnly по умолчанию включён: модели без поддержки инструментов не нужны
 * ни агенту, ни генерации, а в каталоге OpenRouter их под семь десятков —
 * от переводчиков до картиночных, и в списке выбора они только мешают.
 * signal позволяет отменить устаревший запрос при быстром переключении провайдера/фильтра.
 * @param {object} params
 * @param {string} params.provider ключ провайдера (например 'openrouter')
 * @param {boolean} [params.freeOnly]
 * @param {boolean} [params.toolsOnly]
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<LlmModel[]>}
 */
export async function getAiProviderCatalog({
  provider,
  freeOnly = false,
  toolsOnly = true,
  signal,
}) {
  const { data } = await api.get(`${AI_GATEWAY_BASE}/catalog`, {
    params: { provider, freeOnly, toolsOnly },
    signal,
  });
  return data;
}

/**
 * Один элемент истории генераций по ноде (промпт + ответ агента).
 * @typedef {object} NodeHistoryItem
 * @property {number} id
 * @property {string} status completed | failed | ...
 * @property {string|null} task задача пользователя
 * @property {object|null} output ответ агента ({ code, explanation, ... })
 * @property {string|null} model
 * @property {number|null} tokensIn
 * @property {number|null} tokensOut
 * @property {number|null} costUsd стоимость генерации в USD
 * @property {number|null} attempts
 * @property {string} createdAt
 */

/**
 * Страница истории генераций по ноде (курсорная пагинация, новые сверху по id DESC).
 * @typedef {object} NodeHistoryResponse
 * @property {NodeHistoryItem[]} items
 * @property {number|null} nextCursor id для следующей страницы; null = записей больше нет
 */

/**
 * Возвращает страницу истории ИИ-генераций функции конкретной ноды.
 * Первая страница — без cursor; следующая — с nextCursor из прошлого ответа.
 * @param {string} nodeId
 * @param {{ limit?: number, cursor?: number, signal?: AbortSignal }} [options]
 * @returns {Promise<NodeHistoryResponse>}
 */
export async function getNodeAiHistory(nodeId, { limit = 20, cursor, signal } = {}) {
  const params = { limit };
  // первая страница идёт без cursor; на следующих передаём id из nextCursor
  if (cursor) {
    params.cursor = cursor;
  }
  const { data } = await api.get(
    `${AI_GATEWAY_BASE}/agents/workflow-function/nodes/${nodeId}/history`,
    { params, signal },
  );
  return data;
}

/**
 * Возвращает полное состояние асинхронной ИИ-задачи по её id.
 * @returns {Promise<AiJobStatus>}
 */
export async function getAiJob(jobId) {
  const { data } = await api.get(`${AI_GATEWAY_BASE}/jobs/${jobId}`);
  return data;
}

/**
 * Отменяет асинхронную ИИ-задачу: бэк синхронно ставит status='cancelled' и публикует
 * cancel (реальный обрыв LLM-запроса происходит чуть позже на стороне воркера).
 * Идемпотентно: повторная отмена уже отменённого job-а — тоже 200. 409 — job уже в терминале.
 * @returns {Promise<AiJobStatus>}
 */
export async function cancelAiJob(jobId) {
  const { data } = await api.post(`${AI_GATEWAY_BASE}/jobs/${jobId}/cancel`);
  return data;
}

/**
 * Опрашивает статус ИИ-задачи, пока она не достигнет терминального состояния.
 * onStatus вызывается на каждой итерации, signal позволяет отменить ожидание.
 * @param {object} params
 * @param {number} params.jobId
 * @param {number} [params.intervalMs]
 * @param {AbortSignal} [params.signal]
 * @param {(job: AiJobStatus) => void} [params.onStatus]
 * @returns {Promise<AiJobStatus>} финальный объект задачи
 */
export function pollAiJob({ jobId, intervalMs = 1500, signal, onStatus }) {
  return new Promise((resolve, reject) => {
    let timer = null;

    const cleanup = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    const tick = async () => {
      try {
        const job = await getAiJob(jobId);
        onStatus?.(job);
        if (TERMINAL_STATUSES.includes(job.status)) {
          cleanup();
          resolve(job);
          return;
        }
        timer = setTimeout(tick, intervalMs);
      } catch (err) {
        cleanup();
        reject(err);
      }
    };

    tick();
  });
}

/**
 * Результат генерации агента workflow-function.
 * @typedef {object} GenerateResult
 * @property {string|null} code код функции; null = агент отказался/не смог сгенерировать
 * @property {string|null} outputExample пример вывода (строка JSON); null ровно когда code === null
 * @property {string} explanation пояснение агента — присутствует всегда
 * @property {string[]} unresolvedQuestions нерешённые вопросы к пользователю
 */

/**
 * Нормализует output задачи к структуре ответа агента workflow-function.
 * code/outputExample могут быть null (отказ/нельзя — тогда оба null), explanation есть всегда.
 * outputExample приводим к строке JSON, т.к. поле ноды хранит строку (парсится в outputSchema).
 * На случай нестандартного output (например голая строка) остаёмся защитными.
 * @returns {GenerateResult}
 */
export function extractGenerateResult(output) {
  const empty = { code: null, outputExample: null, explanation: '', unresolvedQuestions: [] };
  if (!output) {
    return empty;
  }
  if (typeof output === 'string') {
    return { ...empty, code: output };
  }
  const toExampleString = (value) => {
    if (value === undefined || value === null) {
      return null;
    }
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  };
  return {
    code: typeof output.code === 'string' ? output.code : null,
    outputExample: toExampleString(output.outputExample),
    explanation: typeof output.explanation === 'string' ? output.explanation : '',
    unresolvedQuestions: Array.isArray(output.unresolvedQuestions) ? output.unresolvedQuestions : [],
  };
}
